import React, { useState } from "react";
import Plot from "react-plotly.js";

const BondAnalyzer = () => {
  const [ytm, setYtm] = useState(6);
  const [couponRate, setCouponRate] = useState(6);
  const [maturity, setMaturity] = useState(10);
  const [callPrice, setCallPrice] = useState(1050);
  const [callAfter, setCallAfter] = useState(5);
  const [view, setView] = useState("Price");
  const [bondType, setBondType] = useState("Fixed");

  const faceValue = 1000;
  const ytmRange = Array.from({ length: 200 }, (_, i) => i * 0.1);

  const getCashFlows = () => {
    const cashFlows = [];
    for (let t = 1; t <= maturity; t++) {
      if (bondType === "Zero") {
        cashFlows.push(t === maturity ? faceValue : 0);
      } else {
        const payment = (couponRate / 100) * faceValue;
        if (bondType === "Callable" && t === callAfter) {
          cashFlows.push(payment + callPrice);
          break;
        } else if (t === maturity) {
          cashFlows.push(payment + faceValue);
        } else {
          cashFlows.push(payment);
        }
      }
    }
    return cashFlows;
  };

  const calculatePrice = (rate) => {
    const cashFlows = getCashFlows();
    return cashFlows.reduce(
      (acc, cf, t) => acc + cf / Math.pow(1 + rate / 100, t + 1),
      0
    );
  };

  const calculateDuration = (rate) => {
    const cashFlows = getCashFlows();
    const price = calculatePrice(rate);
    let duration = 0;
    for (let t = 0; t < cashFlows.length; t++) {
      duration += ((t + 1) * cashFlows[t]) / Math.pow(1 + rate / 100, t + 1);
    }
    return duration / price;
  };

  const calculateConvexity = (rate) => {
    const cashFlows = getCashFlows();
    const price = calculatePrice(rate);
    let convexity = 0;
    for (let t = 0; t < cashFlows.length; t++) {
      convexity += ((t + 1) * (t + 2) * cashFlows[t]) / Math.pow(1 + rate / 100, t + 3);
    }
    return convexity / price;
  };

  const priceData = ytmRange.map((rate) => calculatePrice(rate));
  const durationData = ytmRange.map((rate) => calculateDuration(rate));
  const convexityData = ytmRange.map((rate) => calculateConvexity(rate));

  const taylorApprox = (ytmPoint) => {
    const priceAtYtm = calculatePrice(ytmPoint);
    const dur = calculateDuration(ytmPoint);
    const conv = calculateConvexity(ytmPoint);
    return ytmRange.map((r) => {
      const delta = (r - ytmPoint) / 100;
      return priceAtYtm - dur * priceAtYtm * delta + 0.5 * conv * priceAtYtm * delta * delta;
    });
  };

  const plotData = [
    {
      x: ytmRange,
      y: priceData,
      type: "scatter",
      mode: "lines",
      name: "Price vs YTM",
      line: { color: "blue" }
    }
  ];

  if (view === "Duration") {
    plotData.push({
      x: ytmRange,
      y: durationData,
      type: "scatter",
      mode: "lines",
      name: "Duration",
      yaxis: "y2",
      line: { color: "green" }
    });
  }

  if (view === "Convexity") {
    plotData.push({
      x: ytmRange,
      y: convexityData,
      type: "scatter",
      mode: "lines",
      name: "Convexity",
      yaxis: "y2",
      line: { color: "orange" }
    });
    plotData.push({
      x: ytmRange,
      y: taylorApprox(ytm),
      type: "scatter",
      mode: "lines",
      name: "Convexity Approx.",
      line: { dash: "dot", color: "red" }
    });
  }



  return (
    // <div className="p-4 space-y-4">
    //   <h2 className="text-xl font-bold">Bond Analyzer</h2>
    <div className="p-6 max-w-5xl mx-auto">
      {/*<h2 className="text-xl font-bold mb-4">📉 Interactive Bond Analyzer</h2>*/}
      <h2 className="text-xl font-bold mb-4">📊 Interactive Bond Analyzer</h2>
      <style>
        {`
          .bond-analyzer-container {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            width : 1200px;
          }
        
          .sliders-panel {
            flex: 1;
            min-width: 280px;
            max-width: 350px;
            border : solid;
          }
              
          .slider-group {
            margin-bottom: 15px;
          }
        
          .slider-group label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
          }
        
          .radio-section {
            margin-bottom: 20px;
          }
        
          .radio-section label {
            margin-right: 15px;
          }
        
          .radio-buttons {
            margin-top: 5px;
            margin-bottom: 15px;
          }
          .graph-panel {
            flex: 2;
            min-width: 400px;
            max-width: 1000px;
            width : 500px;
            border : solid;
            // margin: auto; /* 👈 center the graph */
          }
        `}
    </style>

      <div className="col-md-6 col-lg-12 wow fadeInUp"  style={{marginBottom:"20px"}} data-wow-delay="0.6s">
        <div className="service-item" >
      <div className="mb-4" >
        <label className="mr-2 font-semibold" style={{backgroundColor:"lightskyblue", marginTop:"10px",margin:"10px"}}>Bond Type:</label>
        {['Fixed', 'Zero', 'Callable'].map(type => (
          <label key={type} className="mr-4"  style={{backgroundColor:"lightblue"}}>
            <input
              type="radio"
              value={type}
              checked={bondType === type}
              onChange={(e) => setBondType(e.target.value)}
              className="mr-1"
            />
            {type}
          </label>
        ))}
      </div>
      <div className="mb-4">
        <label className="mr-2 font-semibold" style={{backgroundColor:"lightskyblue",margin:"10px"}}>Metric:</label>
        {['Price', 'Duration', 'Convexity'].map(type => (
          <label key={type} className="mr-4" style={{backgroundColor:"lightblue"}}>
            <input
              type="radio"
              value={type}
              checked={view === type}
              onChange={(e) => setView(e.target.value)}
              className="mr-1"
            />
            {type}
          </label>
        ))}
      </div>
    {/* LEFT PANEL */}
    {/*<div className="sliders-panel">*/}
    {/*  <div className="slider-group">*/}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/*<div>*/}
        <label style={{backgroundColor:"lightskyblue",margin:"10px"}} >Coupon Rate: {couponRate}%</label>
        <input type="range" min="0" max="20" step="0.1" value={couponRate} onChange={(e) => setCouponRate(Number(e.target.value))} />
        {/*</div>*/}
      {/*</div>*/}

      {/*<div >*/}
        <label style={{backgroundColor:"lightskyblue",margin:"10px"}}>Maturity: {maturity} years</label>
        <input type="range" min="1" max="30" value={maturity} onChange={(e) => setMaturity(Number(e.target.value))} />
      {/*</div>*/}

      {/*<div>*/}
        <label style={{backgroundColor:"lightskyblue",margin:"10px"}}>Yield To Maturity: {ytm}%</label>
        <input type="range" min="0" max="20" step="0.1" value={ytm} onChange={(e) => setYtm(Number(e.target.value))} />
      {/*</div>*/}

        {bondType === 'Callable' && (
          <>
            {/*<div>*/}
              <label style={{backgroundColor:"lightskyblue",margin:"10px"}} className="block font-medium">Call Price: ₹{callPrice}</label>
              <input
                type="range"
                min="900"
                max="1200"
                step="10"
                value={callPrice}
                onChange={(e) => setCallPrice(parseInt(e.target.value))}
                className="w-full"
              />
            {/*</div>*/}

            {/*<div>*/}
              <label style={{backgroundColor:"lightskyblue",margin:"10px"}} className="block font-medium">Call After: {callAfter} years</label>
              <input
                type="range"
                min="1"
                max={maturity}
                step="1"
                value={callAfter}
                onChange={(e) => setCallAfter(parseInt(e.target.value))}
                className="w-full"
              />
            {/*</div>*/}
          </>
        )}

      </div>
        </div>
      </div>

    {/*</div>*/}

    {/* RIGHT PANEL */}
    {/*<div className="graph-panel">*/}
    {/*  <div className="radio-section">*/}
    {/*    <div className="radio-buttons">*/}
    {/*    <label className="font-semibold">Select Bond Type:</label>*/}
    {/*      <label><input type="radio" value="fixed" checked={bondType === "fixed"} onChange={(e) => setBondType(e.target.value)} /> Fixed</label>*/}
    {/*      <label><input type="radio" value="zero" checked={bondType === "zero"} onChange={(e) => setBondType(e.target.value)} /> Zero Coupon</label>*/}
    {/*      <label><input type="radio" value="callable" checked={bondType === "callable"} onChange={(e) => setBondType(e.target.value)} /> Callable</label>*/}
    {/*    </div>*/}
    {/*  </div>*/}
    {/*  <div className="radio-section">*/}
    {/*    <div className="radio-buttons">*/}
    {/*              <label className="font-semibold">Select View:</label>*/}
    {/*      <label><input type="radio" value="price" checked={view === "price"} onChange={(e) => setView(e.target.value)} /> Price</label>*/}
    {/*      <label><input type="radio" value="duration" checked={view === "duration"} onChange={(e) => setView(e.target.value)} /> Duration</label>*/}
    {/*      <label><input type="radio" value="convexity" checked={view === "convexity"} onChange={(e) => setView(e.target.value)} /> Convexity</label>*/}
    {/*    </div>*/}
    {/*  </div>*/}

      <div className="col-md-6 col-lg-12 wow fadeInUp"  style={{marginTop:"20px"}} data-wow-delay="0.6s">
        <div className="service-item">
          <Plot
            data={plotData}
            layout={{
              title: `${view.charAt(0).toUpperCase() + view.slice(1)} vs Yield to Maturity`,
              xaxis: { title: "Yield to Maturity (%)" },
              yaxis: { title: "Price" },
              yaxis2: {
                title: view === "Duration" ? "Duration" : view === "Convexity" ? "Convexity" : undefined,
                overlaying: "y",
                side: "right"
              },
              annotations: [
                {
                  x: ytm,
                  y: calculatePrice(ytm),
                  xref: "x",
                  yref: "y",
                  text: `(YTM: ${ytm}%, Price: ${calculatePrice(ytm).toFixed(2)})`,
                  showarrow: true,
                  arrowhead: 4,
                  ax: 0,
                  ay: -40,
                  font: { color: "red" },
                  arrowcolor: "red"
                },
      ...(view === "Duration"
        ? [{
            x: ytm,
            y: calculateDuration(ytm),
            xref: "x",
            yref: "y2",
            text: `(Mac Duration: ${calculateDuration(ytm).toFixed(2)},Mod Duration: ${(calculateDuration(ytm)/(1+ytm*.01)).toFixed(2)} )`,
            showarrow: true,
            arrowhead: 6,
            ax: 0,
            ay: -40,
            font: { color: "blue" },
            arrowcolor: "blue"
          }]
        : view === "Convexity"
        ? [{
            x: ytm,
            y: calculateConvexity(ytm),
            xref: "x",
            yref: "y2",
            text: `Convexity: ${calculateConvexity(ytm).toFixed(2)}`,
            showarrow: true,
            arrowhead: 6,
            ax: 0,
            ay: -40,
            font: { color: "green" },
            arrowcolor: "green"
          }]
        : [])
              ],
              legend: { x: 0, y: -0.3, orientation: "v" },
              margin: { t: 50, l: 60, r: 60, b: 60 },
            }}
            style={{ width: "100%", height: "100%"}} // 👈 Add maxWidth here
          />
      </div>
      </div>
      {/*      <div className="mt-6">*/}
      {/*  <p><strong>Macaulay Duration:</strong> {macDuration} years</p>*/}
      {/*  <p><strong>Modified Duration:</strong> {duration} years</p>*/}
      {/*</div>*/}
      {/*</div>*/}
    </div>
    // </div>
  );
};

export default BondAnalyzer;

